import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { LogOut, Coins, ArrowLeft, Menu } from 'lucide-react';
import icone from '@/assets/icone.png';

interface LayoutProps {
  children: ReactNode;
  title?: string;
  backTo?: string;
}

export const Layout = ({ children, title, backTo }: LayoutProps) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [nome, setNome] = useState<string>('');
  const [feedbacks, setFeedbacks] = useState<number>(0);
  const [papel, setPapel] = useState<string>('');

  const [empresaId, setEmpresaId] = useState<string | null>(null);

  useEffect(() => {
    const loadUserInfo = async () => {
      if (!user) return;

      // Buscar informações do usuário
      const { data: userData } = await supabase
        .from('usuarios')
        .select('nome, papel, empresa_id')
        .eq('id', user.id)
        .single();

      if (userData) {
        setNome(userData.nome || '');
        setPapel(userData.papel || '');
        setEmpresaId(userData.empresa_id || null);

        // Buscar feedbacks da empresa
        if (userData.empresa_id) {
          const { data: empresaData } = await supabase
            .from('empresas')
            .select('feedbacks')
            .eq('id', userData.empresa_id)
            .single();

          if (empresaData) {
            setFeedbacks(empresaData.feedbacks || 0);
          }
        }
      }
    };

    loadUserInfo();
  }, [user]);

  // Subscrever a mudanças do usuário
  useEffect(() => {
    if (!user?.id) return;

    const channelUsuarios = supabase
      .channel('user-info')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'usuarios',
          filter: `id=eq.${user.id}`,
        },
        (payload: any) => {
          setNome(payload.new.nome || '');
          setPapel(payload.new.papel || '');
          if (payload.new.empresa_id !== empresaId) {
            setEmpresaId(payload.new.empresa_id || null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channelUsuarios);
    };
  }, [user?.id, empresaId]);

  // Subscrever a mudanças de feedbacks da empresa
  useEffect(() => {
    if (!empresaId) return;

    const channelEmpresas = supabase
      .channel(`empresa-feedbacks-${empresaId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'empresas',
          filter: `id=eq.${empresaId}`,
        },
        (payload: any) => {
          setFeedbacks(payload.new.feedbacks || 0);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channelEmpresas);
    };
  }, [empresaId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
      {/* Header */}
      <header className="border-b border-border/50 backdrop-blur-xl bg-card/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => backTo ? navigate(backTo) : navigate(-1)}
              aria-label="Voltar"
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img src={icone} alt="Bianchim Business" className="h-8 sm:h-10 w-auto shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent truncate">
                Bianchim Business
              </h1>
              <p className="hidden sm:block text-xs text-muted-foreground">Treinamento com IA</p>
            </div>
          </div>

          {user && (
            <>
              {/* Desktop */}
              <div className="hidden md:flex items-center gap-4">
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{nome}</span>
                    <Badge variant="outline">
                      {papel === 'admin' ? 'Admin' : papel === 'coordenador' ? 'Coordenador' : 'Vendedor'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                    <Badge variant="secondary" className="gap-1">
                      <Coins className="w-3 h-3" />
                      {feedbacks} feedbacks
                    </Badge>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={signOut} className="gap-2">
                  <LogOut className="w-4 h-4" />
                  Sair
                </Button>
              </div>

              {/* Mobile */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden shrink-0" aria-label="Abrir menu">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[85%] sm:w-80">
                  <div className="flex flex-col gap-6 mt-8">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{nome}</span>
                        <Badge variant="outline">
                          {papel === 'admin' ? 'Admin' : papel === 'coordenador' ? 'Coordenador' : 'Vendedor'}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground break-all">{user.email}</span>
                      <Badge variant="secondary" className="gap-1 w-fit">
                        <Coins className="w-3 h-3" />
                        {feedbacks} feedbacks
                      </Badge>
                    </div>
                    <Button variant="outline" onClick={signOut} className="gap-2 w-full">
                      <LogOut className="w-4 h-4" />
                      Sair
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </>
          )}
        </div>

      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {title && (
          <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            {title}
          </h2>
        )}
        {children}
      </main>
    </div>
  );
};
